// src/pages/admin/manager/evaluaciones/EvaluacionesPage.tsx
import { useState, useEffect, useCallback, useMemo } from "react";
import {
  Stack,
  Button,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  CircularProgress,
  TextField,
  IconButton,
  Chip,
  Menu,
  MenuItem,
  Typography,
  Select,
  FormControl,
  InputLabel,
  Grid,
  Autocomplete,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import BarChartIcon from "@mui/icons-material/BarChart";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import AssignmentIndIcon from "@mui/icons-material/AssignmentInd";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import StopIcon from "@mui/icons-material/Stop";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import ClearIcon from "@mui/icons-material/Clear";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs, { Dayjs } from "dayjs";
import "dayjs/locale/es";
import { useTranslation } from "react-i18next";
import { useSnackbar } from "notistack";
import { useNavigate } from "react-router-dom";
import { useUsuarios, type Usuario } from "../../../../hooks/useUsuarios";
import {
  listEvaluaciones,
  deleteFormulario,
  duplicarFormulario,
  activarFormulario,
  desactivarFormulario,
  type Formulario,
} from "../../../../api/formularios";
import EvaluacionEditorDialog from "../../../../components/formularios/EvaluacionEditorDialog";
import AsignarEvaluacionDialog from "../../../../components/formularios/AsignarEvaluacionDialog";

export default function EvaluacionesPage() {
  const { t } = useTranslation<"translation">();
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();

  const [evaluaciones, setEvaluaciones] = useState<Formulario[]>([]);
  const [loading, setLoading] = useState(false);
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const [selectedEvaluacion, setSelectedEvaluacion] = useState<Formulario | null>(null);
  const [editorOpen, setEditorOpen] = useState(false);
  const [editorEvaluacionId, setEditorEvaluacionId] = useState<number | undefined>();
  const [asignarOpen, setAsignarOpen] = useState(false);
  const [asignarEvaluacionId, setAsignarEvaluacionId] = useState<number | undefined>();

  // Filtros
  const [filtroTitulo, setFiltroTitulo] = useState("");
  const [filtroEstado, setFiltroEstado] = useState("");
  const [filtroCreadoPor, setFiltroCreadoPor] = useState<Usuario | null>(null);
  const [filtroFechaCreacionDesde, setFiltroFechaCreacionDesde] = useState<Dayjs | null>(null);
  const [filtroFechaCreacionHasta, setFiltroFechaCreacionHasta] = useState<Dayjs | null>(null);

  // Obtener lista de usuarios (solo staff, activos)
  const { rows: usuariosStaff } = useUsuarios({
    sinClientes: true,
    soloActivos: true,
    auto: true,
  });

  // Filtrar solo staff (excluir profesor, cliente, consultor)
  const staffUsuarios = useMemo(() => {
    const staffRoles = ['hr', 'att_familias', 'att_alumnado', 'att_profesorado', 'facturacion', 'admisiones', 'admin'];
    return usuariosStaff
      .filter((u) => staffRoles.includes(u.rol))
      .sort((a, b) => {
        const nameA = a.nombreCompleto || a.email || '';
        const nameB = b.nombreCompleto || b.email || '';
        return nameA.localeCompare(nameB);
      });
  }, [usuariosStaff]);

  // Estados disponibles
  const estados = [
    { value: "borrador", label: "Borrador" },
    { value: "activo", label: "Activo" },
    { value: "desactivado", label: "Desactivado" },
    { value: "cerrado", label: "Cerrado" },
  ];

  const loadEvaluaciones = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await listEvaluaciones({ limit: 100 });
      setEvaluaciones(data || []);
    } catch (error: any) {
      enqueueSnackbar("Error al cargar evaluaciones", { variant: "error" });
    } finally {
      setLoading(false);
    }
  }, [enqueueSnackbar]);

  useEffect(() => {
    loadEvaluaciones();
  }, [loadEvaluaciones]);

  const handleDelete = async (id: number) => {
    if (!window.confirm("¿Estás seguro de eliminar esta evaluación?")) return;
    try {
      await deleteFormulario(id);
      enqueueSnackbar("Evaluación eliminada", { variant: "success" });
      loadEvaluaciones();
    } catch (error: any) {
      enqueueSnackbar("Error al eliminar evaluación", { variant: "error" });
    }
  };

  const handleDuplicate = async (id: number) => {
    try {
      await duplicarFormulario(id);
      enqueueSnackbar("Evaluación duplicada", { variant: "success" });
      loadEvaluaciones();
    } catch (error: any) {
      enqueueSnackbar("Error al duplicar evaluación", { variant: "error" });
    }
  };

  const handleActivate = async (id: number) => {
    try {
      await activarFormulario(id);
      enqueueSnackbar("Evaluación activada", { variant: "success" });
      loadEvaluaciones();
    } catch (error: any) {
      enqueueSnackbar("Error al activar evaluación", { variant: "error" });
    }
  };

  const handleDeactivate = async (id: number) => {
    try {
      await desactivarFormulario(id);
      enqueueSnackbar("Evaluación desactivada", { variant: "success" });
      loadEvaluaciones();
    } catch (error: any) {
      enqueueSnackbar("Error al desactivar evaluación", { variant: "error" });
    }
  };

  const handleEdit = (id: number) => {
    setEditorEvaluacionId(id);
    setEditorOpen(true);
  };

  const handleCreate = () => {
    setEditorEvaluacionId(undefined);
    setEditorOpen(true);
  };

  const handleAssign = (id: number) => {
    setAsignarEvaluacionId(id);
    setAsignarOpen(true);
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, evaluacion: Formulario) => {
    setMenuAnchor(event.currentTarget);
    setSelectedEvaluacion(evaluacion);
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
    setSelectedEvaluacion(null);
  };

  const handleMetricas = (id: number) => {
    navigate(`/admin/manager/evaluaciones/${id}/metricas`);
  };

  const getEstadoChipSx = (estado: string) => {
    switch (estado) {
      case 'eliminado':
        return { backgroundColor: '#BD0000', color: '#FFFFFF', fontWeight: 'medium' };
      case 'borrador':
        return { backgroundColor: '#FB82CD', color: '#FFFFFF', fontWeight: 'medium' };
      case 'desactivado':
        return { backgroundColor: '#CBCBCB', color: '#4A4949', fontWeight: 'medium' };
      case 'activo':
        return { backgroundColor: (theme: any) => theme.palette.success.main, color: '#FFFFFF', fontWeight: 'medium' };
      case 'cerrado':
        return { backgroundColor: (theme: any) => theme.palette.info.main, color: '#FFFFFF', fontWeight: 'medium' };
      case 'completado':
        return { backgroundColor: (theme: any) => theme.palette.success.main, color: '#FFFFFF', fontWeight: 'medium' };
      default:
        return {};
    }
  };

  const tieneFiltrosActivos =
    filtroTitulo !== "" ||
    filtroEstado !== "" ||
    filtroCreadoPor !== null ||
    filtroFechaCreacionDesde !== null ||
    filtroFechaCreacionHasta !== null;

  const limpiarFiltros = () => {
    setFiltroTitulo("");
    setFiltroEstado("");
    setFiltroCreadoPor(null);
    setFiltroFechaCreacionDesde(null);
    setFiltroFechaCreacionHasta(null);
  };

  const filtered = evaluaciones.filter((e) => {
    // Filtro por título
    if (filtroTitulo) {
      const s = filtroTitulo.toLowerCase();
      if (!e.titulo.toLowerCase().includes(s)) return false;
    }

    // Filtro por estado
    if (filtroEstado) {
      if (e.estado !== filtroEstado) return false;
    }

    // Filtro por creado por
    if (filtroCreadoPor) {
      if (e.creadoPor !== filtroCreadoPor.id) return false;
    }

    // Filtro por fecha de creación (rango)
    if (filtroFechaCreacionDesde || filtroFechaCreacionHasta) {
      if (!e.createdAt) return false;
      const fechaCreacion = dayjs(e.createdAt);
      if (filtroFechaCreacionDesde && fechaCreacion.isBefore(filtroFechaCreacionDesde, "day")) {
        return false;
      }
      if (filtroFechaCreacionHasta && fechaCreacion.isAfter(filtroFechaCreacionHasta, "day")) {
        return false;
      }
    }

    return true;
  });

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="es">
      <Stack spacing={2}>
        <Typography variant="h4" sx={{ fontWeight: "bold" }}>
          Gestión de Evaluaciones
        </Typography>

        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Stack direction="row" spacing={2}>
            <Button startIcon={<AddIcon />} variant="contained" onClick={handleCreate}>
              Crear evaluación
            </Button>
            <Button
              variant="outlined"
              onClick={() => navigate("/admin/manager/evaluaciones/pendientes")}
              startIcon={<WarningAmberIcon />}
            >
              Ver Pendientes
            </Button>
          </Stack>
        </Stack>

        {/* Filtros */}
        <Paper sx={{ p: 2 }}>
          <Typography variant="h6" sx={{ mb: 2, fontWeight: "bold" }}>
            Filtros
          </Typography>
          {/* Fila 1: Filtros generales */}
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                label="Por título"
                size="small"
                fullWidth
                value={filtroTitulo}
                onChange={(e) => setFiltroTitulo(e.target.value)}
                sx={{ mb: 0 }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <FormControl fullWidth size="small">
                <InputLabel>Por estado</InputLabel>
                <Select
                  value={filtroEstado}
                  label="Por estado"
                  onChange={(e) => setFiltroEstado(e.target.value)}
                >
                  <MenuItem value="">Todos</MenuItem>
                  {estados.map((est) => (
                    <MenuItem key={est.value} value={est.value}>
                      {est.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <Autocomplete
                options={staffUsuarios}
                getOptionLabel={(option) => {
                  if (typeof option === 'string') return option;
                  return option.nombreCompleto || option.email || '';
                }}
                value={filtroCreadoPor}
                onChange={(_, newValue) => {
                  // Solo aceptar Usuario, no strings libres
                  if (newValue && typeof newValue === 'object') {
                    setFiltroCreadoPor(newValue);
                  } else {
                    setFiltroCreadoPor(null);
                  }
                }}
                freeSolo
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Creado por"
                    size="small"
                    sx={{ mb: 0 }}
                  />
                )}
                filterOptions={(options, params) => {
                  const filtered = options.filter((option) => {
                    const searchText = params.inputValue.toLowerCase();
                    const nombre = (option.nombreCompleto || '').toLowerCase();
                    const email = (option.email || '').toLowerCase();
                    return nombre.includes(searchText) || email.includes(searchText);
                  });
                  return filtered;
                }}
              />
            </Grid>
          </Grid>

          {/* Fila 2: Filtros de fecha */}
          <Stack
            direction="row"
            spacing={2}
            alignItems="center"
            sx={{ mt: 2 }}
          >
            <DatePicker
              label="Creados desde"
              value={filtroFechaCreacionDesde}
              onChange={(newValue) => setFiltroFechaCreacionDesde(newValue)}
              format="DD/MM/YYYY"
              slotProps={{ textField: { size: "small", sx: { width: 180 } } }}
            />
            <Typography variant="body2" color="text.secondary">
              hasta
            </Typography>
            <DatePicker
              label="Creados hasta"
              value={filtroFechaCreacionHasta}
              onChange={(newValue) => setFiltroFechaCreacionHasta(newValue)}
              format="DD/MM/YYYY"
              slotProps={{ textField: { size: "small", sx: { width: 180 } } }}
            />
            {tieneFiltrosActivos && (
              <Button
                startIcon={<ClearIcon />}
                onClick={limpiarFiltros}
                variant="outlined"
                size="small"
              >
                Limpiar
              </Button>
            )}
          </Stack>
        </Paper>

      <Paper>
        {loading ? (
          <Stack alignItems="center" py={4}>
            <CircularProgress />
          </Stack>
        ) : (
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Título</TableCell>
                <TableCell>Descripción</TableCell>
                <TableCell>Creado por</TableCell>
                <TableCell>Estado</TableCell>
                <TableCell>Puntuación Máx.</TableCell>
                <TableCell>Nota Mínima</TableCell>
                <TableCell>Roles</TableCell>
                <TableCell align="right">Acciones</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filtered.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    No hay evaluaciones
                  </TableCell>
                </TableRow>
              ) : (
                filtered.map((evaluacion: Formulario) => (
                  <TableRow key={evaluacion.id}>
                    <TableCell>{evaluacion.titulo}</TableCell>
                    <TableCell>{evaluacion.descripcion || "-"}</TableCell>
                    <TableCell>
                      {evaluacion.creadoPorUsuario?.nombreCompleto || "-"}
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={evaluacion.estado} 
                        size="small" 
                        sx={getEstadoChipSx(evaluacion.estado)} 
                      />
                    </TableCell>
                    <TableCell>
                      {evaluacion.puntuacionMaxima || "-"}
                    </TableCell>
                    <TableCell>
                      {evaluacion.notaMinima || "-"}
                    </TableCell>
                    <TableCell>
                      {evaluacion.rolesPermitidos && evaluacion.rolesPermitidos.length > 0
                        ? evaluacion.rolesPermitidos.join(", ")
                        : "Todos"}
                    </TableCell>
                    <TableCell align="right">
                      <IconButton
                        size="small"
                        onClick={() => handleMetricas(evaluacion.id)}
                        title="Ver métricas"
                      >
                        <BarChartIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleAssign(evaluacion.id)}
                        title="Asignar individualmente"
                      >
                        <AssignmentIndIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={(e) => handleMenuOpen(e, evaluacion)}
                      >
                        <MoreVertIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        )}
      </Paper>

      <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={handleMenuClose}>
        {selectedEvaluacion && (
          <>
            <MenuItem onClick={() => {
              handleEdit(selectedEvaluacion.id);
              handleMenuClose();
            }}>
              <EditIcon sx={{ mr: 1 }} fontSize="small" />
              Editar
            </MenuItem>
            <MenuItem onClick={() => {
              handleDuplicate(selectedEvaluacion.id);
              handleMenuClose();
            }}>
              <ContentCopyIcon sx={{ mr: 1 }} fontSize="small" />
              Duplicar
            </MenuItem>
            {selectedEvaluacion.estado === 'activo' ? (
              <MenuItem onClick={() => {
                handleDeactivate(selectedEvaluacion.id);
                handleMenuClose();
              }}>
                <StopIcon sx={{ mr: 1 }} fontSize="small" />
                Desactivar
              </MenuItem>
            ) : (
              <MenuItem onClick={() => {
                handleActivate(selectedEvaluacion.id);
                handleMenuClose();
              }}>
                <PlayArrowIcon sx={{ mr: 1 }} fontSize="small" />
                Activar
              </MenuItem>
            )}
            <MenuItem onClick={() => {
              if (selectedEvaluacion) {
                handleDelete(selectedEvaluacion.id);
              }
              handleMenuClose();
            }}>
              <DeleteIcon sx={{ mr: 1 }} fontSize="small" />
              Eliminar
            </MenuItem>
          </>
        )}
      </Menu>

      <EvaluacionEditorDialog
        open={editorOpen}
        evaluacionId={editorEvaluacionId}
        onClose={() => {
          setEditorOpen(false);
          setEditorEvaluacionId(undefined);
        }}
        onSaved={loadEvaluaciones}
      />

      <AsignarEvaluacionDialog
        open={asignarOpen}
        evaluacionId={asignarEvaluacionId!}
        onClose={() => {
          setAsignarOpen(false);
          setAsignarEvaluacionId(undefined);
        }}
        onSaved={loadEvaluaciones}
      />
      </Stack>
    </LocalizationProvider>
  );
}

